import { useEffect, useState } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import api from '../utils/api'
import { useAuth } from '../hooks/useAuth'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import StatusIndicator from '../components/StatusIndicator'

interface DarkWebHit {
  url: string
  domain: string
  title: string
  category: string
  snippet: string
  identifier_matched: string
  confidence: 'HIGH' | 'MEDIUM' | 'LOW'
  updated_on: string
  relevance_score: number
}

interface DarkWebResult {
  subject: string
  total_hits: number
  dump_hits: number
  results: DarkWebHit[]
  sites_searched: number
  timestamp: string
}

interface Order {
  id: string
  tier: string
  status: 'pending' | 'paid' | 'awaiting_intake' | 'processing' | 'complete'
  created_at: string
  report_id: string | null
  intake_complete: boolean
}

export default function Dashboard() {
  const { user } = useAuth()
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [searchParams, setSearchParams] = useSearchParams()
  const [verifyMessage, setVerifyMessage] = useState('')
  const navigate = useNavigate()

  // Dark web exposure check
  const [dwName, setDwName] = useState('')
  const [dwAliases, setDwAliases] = useState('')
  const [dwLoading, setDwLoading] = useState(false)
  const [dwResult, setDwResult] = useState<DarkWebResult | null>(null)
  const [dwError, setDwError] = useState('')

  useEffect(() => {
    async function verifyAndFetchOrders() {
      // Check if returning from Stripe checkout
      const orderId = searchParams.get('order')
      if (orderId) {
        try {
          // Verify the payment with Stripe
          const verifyResponse = await api.post(`/order/${orderId}/verify`)

          if (verifyResponse.data.needs_intake) {
            // Redirect to subject intake form
            navigate(`/intake/${orderId}`)
            return
          } else if (verifyResponse.data.status === 'paid') {
            setVerifyMessage('Payment successful! Your report is being generated.')
          }
        } catch {
          // Ignore verification errors
        }
        // Clear the order param from URL
        setSearchParams({})
      }

      // Check for started param (coming from intake)
      if (searchParams.get('started')) {
        setVerifyMessage('Scan started! Your report will be ready soon.')
        setSearchParams({})
      }

      // Fetch orders
      try {
        const response = await api.get('/orders')
        setOrders(response.data)
      } catch {
        // Handle error silently
      } finally {
        setLoading(false)
      }
    }

    verifyAndFetchOrders()
  }, [searchParams, setSearchParams, navigate])

  async function runDarkWebCheck() {
    if (!dwName.trim()) return
    setDwLoading(true)
    setDwError('')
    setDwResult(null)
    try {
      const params = new URLSearchParams({ name: dwName.trim() })
      if (user?.email) params.append('email', user.email)
      if (dwAliases.trim()) params.append('aliases', dwAliases.trim())
      const res = await api.post(`/darkweb/monitor-subject?${params.toString()}`)
      setDwResult(res.data)
    } catch {
      setDwError('Unable to run exposure check. Please try again.')
    } finally {
      setDwLoading(false)
    }
  }

  const tierLabels: Record<string, string> = {
    free: 'Exposure Snapshot',
    quick: 'Exposure Report',
    full: 'Executive Intelligence Report',
    annual: 'Annual Monitoring',
    enterprise: 'Enterprise Package',
  }

  return (
    <div className="min-h-screen">
      <Navbar />

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8 flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Dashboard</h1>
              <p className="text-dark-400">
                Welcome back, {user?.email}
              </p>
            </div>
            <StatusIndicator />
          </div>

          {verifyMessage && (
            <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-lg text-green-400">
              {verifyMessage}
            </div>
          )}

          {/* Quick Actions */}
          <div className="grid md:grid-cols-4 gap-4 mb-12">
            <Link to="/#pricing" className="card hover:border-ginger-500 transition-colors">
              <div className="text-2xl mb-2">📊</div>
              <h3 className="font-bold mb-1">New Assessment</h3>
              <p className="text-dark-400 text-sm">Order a new risk assessment</p>
            </Link>
            <Link to="/sample-report" className="card hover:border-ginger-500 transition-colors bg-ginger-500/5 border-ginger-500/30">
              <div className="text-2xl mb-2">📄</div>
              <h3 className="font-bold mb-1">Sample Report</h3>
              <p className="text-dark-400 text-sm">See a full $2,500 report</p>
            </Link>
            <a href="mailto:services@monadnockcyber.ai" className="card hover:border-ginger-500 transition-colors">
              <div className="text-2xl mb-2">💬</div>
              <h3 className="font-bold mb-1">Contact Support</h3>
              <p className="text-dark-400 text-sm">Get help with your reports</p>
            </a>
            <Link to="/optouts" className="card hover:border-ginger-500 transition-colors">
              <div className="text-2xl mb-2">🧹</div>
              <h3 className="font-bold mb-1">Opt-Out Tracker</h3>
              <p className="text-dark-400 text-sm">Remove your data from brokers</p>
            </Link>
          </div>

          {/* Orders */}
          <div className="card">
            <h2 className="text-xl font-bold mb-6">Your Reports</h2>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-ginger-500 mx-auto"></div>
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-4">📋</div>
                <h3 className="font-bold mb-2">No reports yet</h3>
                <p className="text-dark-400 mb-4">
                  Order your first assessment to see your digital exposure.
                </p>
                <Link to="/#pricing" className="btn btn-primary">
                  Get Started
                </Link>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-dark-700">
                      <th className="text-left py-3 px-4 text-dark-400 font-medium">Report</th>
                      <th className="text-left py-3 px-4 text-dark-400 font-medium">Date</th>
                      <th className="text-left py-3 px-4 text-dark-400 font-medium">Status</th>
                      <th className="text-left py-3 px-4 text-dark-400 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.id} className="border-b border-dark-800">
                        <td className="py-3 px-4 font-medium">
                          {tierLabels[order.tier] || order.tier}
                        </td>
                        <td className="py-3 px-4 text-dark-400">
                          {new Date(order.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`text-xs px-2 py-1 rounded ${
                            order.status === 'complete' ? 'bg-green-500/20 text-green-400' :
                            order.status === 'processing' ? 'bg-yellow-500/20 text-yellow-400' :
                            order.status === 'paid' ? 'bg-yellow-500/20 text-yellow-400' :
                            order.status === 'awaiting_intake' ? 'bg-ginger-500/20 text-ginger-400' :
                            'bg-dark-700 text-dark-400'
                          }`}>
                            {order.status === 'complete' ? 'Complete' :
                             order.status === 'processing' ? 'Processing' :
                             order.status === 'paid' ? 'Processing' :
                             order.status === 'awaiting_intake' ? 'Needs Info' : 'Pending'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {order.status === 'awaiting_intake' || (order.status === 'paid' && !order.intake_complete) ? (
                            <Link
                              to={`/intake/${order.id}`}
                              className="text-ginger-500 hover:underline text-sm font-medium"
                            >
                              Complete Setup →
                            </Link>
                          ) : order.report_id ? (
                            <Link
                              to={`/report/${order.report_id}`}
                              className="text-ginger-500 hover:underline text-sm"
                            >
                              View Report →
                            </Link>
                          ) : (
                            <span className="text-dark-500 text-sm">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Dark Web Exposure Check */}
          <div className="card mt-8">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <h2 className="text-xl font-bold">Dark Web Exposure Check</h2>
            </div>
            <p className="text-dark-400 text-sm mb-6">
              We'll search our indexed dark web corpus — {dwResult ? dwResult.sites_searched.toLocaleString() : '20,000+'} pages — for your name and email. Results show context only, never raw dump content.
            </p>

            <div className="grid md:grid-cols-[1fr_1fr_auto] gap-3 mb-4">
              <div>
                <label className="block text-xs text-dark-400 mb-1 font-mono uppercase tracking-wider">Full Name *</label>
                <input
                  type="text"
                  placeholder="Jane Smith"
                  value={dwName}
                  onChange={e => setDwName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && runDarkWebCheck()}
                  className="w-full bg-dark-800 border border-dark-700 rounded-lg px-3 py-2 text-sm text-dark-100 focus:outline-none focus:border-ginger-500"
                />
              </div>
              <div>
                <label className="block text-xs text-dark-400 mb-1 font-mono uppercase tracking-wider">Aliases / Usernames (optional, comma-sep)</label>
                <input
                  type="text"
                  placeholder="jsmith, jane.smith88"
                  value={dwAliases}
                  onChange={e => setDwAliases(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && runDarkWebCheck()}
                  className="w-full bg-dark-800 border border-dark-700 rounded-lg px-3 py-2 text-sm text-dark-100 focus:outline-none focus:border-ginger-500"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={runDarkWebCheck}
                  disabled={dwLoading || !dwName.trim()}
                  className="btn btn-primary text-sm px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {dwLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                      Scanning...
                    </span>
                  ) : 'Run Check'}
                </button>
              </div>
            </div>

            <p className="text-xs text-dark-600 mb-6">Your account email ({user?.email}) is automatically included in the search.</p>

            {dwError && (
              <div className="p-3 bg-red-950/40 border border-red-500/30 rounded-lg text-red-400 text-sm mb-4">{dwError}</div>
            )}

            {dwResult && (
              <div>
                {/* Summary bar */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className={`rounded-lg border p-4 text-center ${dwResult.dump_hits > 0 ? 'border-red-500/40 bg-red-950/20' : 'border-green-500/30 bg-green-950/20'}`}>
                    <div className={`text-2xl font-black ${dwResult.dump_hits > 0 ? 'text-red-400' : 'text-green-400'}`}>{dwResult.dump_hits}</div>
                    <div className="text-xs text-dark-400 mt-1">Dump Site Hits</div>
                  </div>
                  <div className="rounded-lg border border-dark-700 bg-dark-900 p-4 text-center">
                    <div className="text-2xl font-black text-dark-100">{dwResult.total_hits}</div>
                    <div className="text-xs text-dark-400 mt-1">Total Matches</div>
                  </div>
                  <div className="rounded-lg border border-dark-700 bg-dark-900 p-4 text-center">
                    <div className="text-2xl font-black text-dark-100">{dwResult.sites_searched.toLocaleString()}</div>
                    <div className="text-xs text-dark-400 mt-1">Pages Searched</div>
                  </div>
                </div>

                {dwResult.results.length === 0 ? (
                  <div className="text-center py-8 border border-green-500/20 bg-green-950/10 rounded-xl">
                    <div className="text-3xl mb-2">✓</div>
                    <div className="text-green-400 font-bold">No exposure detected</div>
                    <p className="text-dark-400 text-sm mt-1">Your identifiers were not found in our indexed dark web corpus.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <h3 className="text-sm font-mono font-bold text-dark-400 uppercase tracking-widest">Exposure Findings</h3>
                    {dwResult.results.map((hit, i) => (
                      <div key={i} className={`rounded-lg border p-4 ${
                        hit.confidence === 'HIGH' ? 'border-red-500/40 bg-red-950/10' :
                        hit.confidence === 'MEDIUM' ? 'border-orange-500/30 bg-orange-950/10' :
                        'border-dark-700 bg-dark-900'
                      }`}>
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div>
                            <div className="font-mono text-xs text-dark-500 mb-0.5">{hit.domain}</div>
                            <div className="text-sm font-semibold text-dark-100">{hit.title}</div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded border ${
                              hit.confidence === 'HIGH' ? 'text-red-400 border-red-500/40 bg-red-950/30' :
                              hit.confidence === 'MEDIUM' ? 'text-orange-400 border-orange-500/40 bg-orange-950/30' :
                              'text-dark-400 border-dark-700 bg-dark-800'
                            }`}>{hit.confidence}</span>
                            <span className="text-xs text-dark-500 bg-dark-800 border border-dark-700 px-2 py-0.5 rounded">{hit.category.replace('_', ' ')}</span>
                          </div>
                        </div>
                        {hit.snippet && (
                          <p className="text-xs text-dark-400 font-mono bg-dark-950 rounded p-2 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: hit.snippet.replace(/\[MATCH\]/g, '<mark class="bg-red-900/60 text-red-300 rounded px-0.5">').replace(/\[\/MATCH\]/g, '</mark>') }}
                          />
                        )}
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-xs text-dark-600">Matched: <span className="text-dark-400">{hit.identifier_matched}</span></span>
                          {hit.updated_on && <span className="text-xs text-dark-600">Indexed: {new Date(hit.updated_on).toLocaleDateString()}</span>}
                        </div>
                      </div>
                    ))}

                    {dwResult.dump_hits > 0 && (
                      <div className="mt-4 p-4 bg-ginger-950/20 border border-ginger-500/30 rounded-lg">
                        <p className="text-sm text-ginger-400 font-semibold mb-1">Remediation recommended</p>
                        <p className="text-xs text-dark-400">Your identifiers appear on known data dump sites. Consider ordering an Executive Intelligence Report for full remediation guidance, or contact <a href="mailto:services@monadnockcyber.ai" className="text-ginger-400 hover:underline">services@monadnockcyber.ai</a>.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

        </div>
      </div>

      <Footer />
    </div>
  )
}
